import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import useBoundStore from "@/services/store/BoundStore";
import useAPI from "@/services/api/hook";
import { ReportType, SettingsPeriod } from "@/entities/reporting/types";
import { isEmpty } from "lodash";

const EMPTY_ARRAY: UserData[] = [];

interface BaseBreadcrumbsContextType<TDirectReportsResponse> {
  selectedUserId: string;
  setSelectedUserId: (id: string) => void;
  dynamicTableData: UserData[];
  breadcrumbIds: string[];
  handleRowClick: (rowData: UserData) => void;
  directReportsData?: TDirectReportsResponse;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

interface BaseBreadcrumbsProviderProps {
  children: React.ReactNode;
  leadUser: UserData | undefined;
  period?: SettingsPeriod;
  reportType: ReportType;
  defaultPeriod: SettingsPeriod;
  responseKey: string;
  enableDirectReportsData?: boolean;
}

export function createBreadcrumbsContext<TDirectReportsResponse>() {
  const BreadcrumbsContext = createContext<
    BaseBreadcrumbsContextType<TDirectReportsResponse> | undefined
  >(undefined);

  const BreadcrumbsProvider: React.FC<BaseBreadcrumbsProviderProps> = ({
    children,
    leadUser,
    period,
    reportType,
    defaultPeriod,
    responseKey,
    enableDirectReportsData = true
  }) => {
    const getUser = useBoundStore((state) => state.users.getEntity);
    const [selectedUserId, setSelectedUserId] = useState<string>(
      ReservedUserIds.UNKNOWN_USER_ID
    );

    useEffect(() => {
      setSelectedUserId(
        leadUser ? leadUser.id : ReservedUserIds.UNKNOWN_USER_ID
      );
    }, [leadUser]);

    const selectedUser = useUsers([selectedUserId]).users[0];
    const directReports = useMemo(() => {
      return selectedUser?.directReports || [];
    }, [selectedUser]);

    const directReportsDataUsers = useUsers(directReports).users;

    const { data, isLoading, error, refetch } = useAPI(
      "reports/type/directReports",
      {
        pathParams: { type: reportType.toString() },
        queryParams: {
          period: (period ?? defaultPeriod).toString(),
          user: selectedUserId
        }
      },
      {
        retry: false,
        enabled:
          enableDirectReportsData &&
          selectedUserId !== ReservedUserIds.UNKNOWN_USER_ID &&
          !isEmpty(directReports)
      }
    );

    const responseData = useMemo(() => {
      return data?.[responseKey]?.directReports as TDirectReportsResponse;
    }, [data, responseKey]);

    const handleRowClick = useCallback((rowData: UserData) => {
      if (rowData && rowData.directReports && rowData.directReports.length > 0)
        setSelectedUserId(rowData.id);
    }, []);

    const dynamicTableData: UserData[] = useMemo(() => {
      if (!directReportsDataUsers || directReportsDataUsers.length === 0)
        return EMPTY_ARRAY;
      return directReportsDataUsers;
    }, [directReportsDataUsers]);

    const buildBreadcrumbPath = useCallback(
      (
        user: UserData | undefined,
        leadUserParam: UserData | undefined,
        path: string[] = []
      ): string[] => {
        if (!user || !leadUserParam) {
          return path;
        }
        path.unshift(user.id);
        if (user.id === leadUserParam.id || !user.managerId) {
          return path;
        }
        const manager = getUser(user.managerId);
        return buildBreadcrumbPath(manager, leadUserParam, path);
      },
      [getUser]
    );

    const breadcrumbIds = useMemo(
      () => buildBreadcrumbPath(selectedUser, leadUser),
      [selectedUser, leadUser, buildBreadcrumbPath]
    );

    const value = useMemo(
      () => ({
        selectedUserId,
        setSelectedUserId,
        dynamicTableData,
        breadcrumbIds,
        handleRowClick,
        directReportsData: responseData,
        isLoading,
        error,
        refetch
      }),
      [
        selectedUserId,
        dynamicTableData,
        breadcrumbIds,
        handleRowClick,
        responseData,
        isLoading,
        error,
        refetch
      ]
    );

    return (
      <BreadcrumbsContext.Provider value={value}>
        {children}
      </BreadcrumbsContext.Provider>
    );
  };

  const useBreadcrumbs = () => {
    const context = useContext(BreadcrumbsContext);
    if (context === undefined) {
      throw new Error(
        "useBreadcrumbs must be used within a BreadcrumbsProvider"
      );
    }
    return context;
  };

  return { BreadcrumbsProvider, useBreadcrumbs };
}
